import { createGlobalStyle } from "styled-components";

const GlobalStyles = createGlobalStyle`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  :root {
    --toastify-font-family: "Roboto";
    /* Variables CSS para mejor rendimiento */
    --safe-area-inset-top: env(safe-area-inset-top);
    --safe-area-inset-bottom: env(safe-area-inset-bottom);
    --safe-area-inset-left: env(safe-area-inset-left);
    --safe-area-inset-right: env(safe-area-inset-right);
    /* Variables para scroll suave */
    --scroll-behavior: smooth;
    /* Variables para mejor rendimiento */
    --will-change: auto;
  }

  html {
    /* Mejor scroll suave */
    scroll-behavior: var(--scroll-behavior);
    /* Prevenir zoom en iOS */
    -webkit-text-size-adjust: 100%;
    /* Mejor renderizado */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    /* Mejor rendimiento */
    will-change: var(--will-change);
  }

  body {
    margin: 0;
    font-family: "Roboto", "Basier circle", Geneva, Tahoma, sans-serif;
    line-height: 1.6;
    /* Mejor contraste y legibilidad */
    color: #1a1a1a;
    background-color: #ffffff;
    /* Prevenir scroll horizontal */
    overflow-x: hidden;
    /* Mejor rendimiento en móviles */
    -webkit-overflow-scrolling: touch;
    /* Mejor rendimiento */
    will-change: var(--will-change);
  }

  /* Mejorar accesibilidad de enlaces */
  a,
  a:focus,
  a:active,
  a:visited {
    color: inherit;
    text-decoration: none;
    /* Mejor focus visible */
    outline: 2px solid transparent;
    outline-offset: 2px;
    transition: outline-color 0.2s ease, color 0.2s ease;
  }

  a:focus-visible {
    outline-color: #4a7c59;
  }

  a:hover {
    color: #4a7c59;
  }

  /* Reset de headings */
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    font-weight: inherit;
    margin-block-start: 0;
    margin-block-end: 0;
    line-height: 1.2;
    color: inherit;
  }

  /* Mejorar formularios */
  button,
  input,
  textarea,
  select {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }

  button {
    cursor: pointer;
    padding: 0;
    border: none;
    color: inherit;
    background: none;
    /* Mejor accesibilidad */
    min-height: 44px;
    min-width: 44px;
    /* Prevenir selección de texto */
    user-select: none;
    /* Mejor focus */
    outline: 2px solid transparent;
    outline-offset: 2px;
    transition: outline-color 0.2s ease, transform 0.2s ease;
    /* Mejor rendimiento */
    will-change: transform;
  }

  button:focus-visible {
    outline-color: #4a7c59;
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none !important;
  }

  /* Mejorar inputs */
  input,
  textarea,
  select {
    border: 1px solid #c1c7cd;
    border-radius: 8px;
    padding: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    background-color: #ffffff;
    color: #1a1a1a;
  }

  input:focus,
  textarea:focus,
  select:focus {
    outline: none;
    border-color: #4a7c59;
    box-shadow: 0 0 0 3px rgba(74, 124, 89, 0.1);
  }

  input:disabled,
  textarea:disabled,
  select:disabled {
    background-color: #f1f3f4;
    color: #b0b0b0;
    cursor: not-allowed;
  }

  /* Reset de listas */
  ul,
  li {
    list-style: none;
    padding-inline-start: 0px;
    padding-left: 0;
    margin: 0;
  }

  /* Mejorar imágenes */
  img {
    max-width: 100%;
    height: auto;
    display: block;
    /* Mejor rendimiento */
    will-change: transform;
  }

  /* Mejorar scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  /* Scrollbar para Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
  }

  /* Clases de utilidad */
  .odibee-sans-regular {
    font-family: "Odibee Sans", sans-serif;
    font-weight: 400;
    font-style: normal;
  }

  .mansalva-regular {
    font-family: "Mansalva", sans-serif;
    font-weight: 400;
    font-style: normal;
  }

  /* Utilidades de accesibilidad */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Utilidades de layout */
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 16px;

    @media (min-width: 768px) {
      padding: 0 24px;
    }

    @media (min-width: 1024px) {
      padding: 0 32px;
    }
  }

  .visually-hidden {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }

  /* Mejorar Toastify */
  .Toastify__toast {
    border-radius: 12px;
    font-family: "Roboto", sans-serif;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .Toastify__close-button > svg {
    min-width: 42px;
    min-height: 42px;
  }

  .Toastify__toast-body {
    font-size: 14px;
    line-height: 1.5;
  }

  /* Mejorar mapa */
  .leaflet-container {
    font-family: "Roboto", sans-serif;
  }

  .leaflet-popup-content-wrapper {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .leaflet-popup-content {
    margin: 12px;
    font-size: 14px;
    line-height: 1.5;
  }

  .leaflet-popup-close-button {
    font-size: 18px;
    color: #666;
    transition: color 0.2s ease;
  }

  .leaflet-popup-close-button:hover {
    color: #333;
  }

  /* Mejorar accesibilidad del focus */
  *:focus-visible {
    outline: 2px solid #4a7c59;
    outline-offset: 2px;
  }

  /* Utilidades de animación */
  .fade-in {
    animation: fadeIn 0.3s ease-in-out;
  }

  .slide-up {
    animation: slideUp 0.3s ease-out;
  }

  .scale-in {
    animation: scaleIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes scaleIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* Utilidades de responsive */
  .mobile-only {
    @media (min-width: 768px) {
      display: none !important;
    }
  }

  .tablet-up {
    display: none;
    @media (min-width: 768px) {
      display: block;
    }
  }

  .desktop-up {
    display: none;
    @media (min-width: 1024px) {
      display: block;
    }
  }

  /* Utilidades de texto */
  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Utilidades de espaciado */
  .mb-0 { margin-bottom: 0; }
  .mb-1 { margin-bottom: 0.25rem; }
  .mb-2 { margin-bottom: 0.5rem; }
  .mb-3 { margin-bottom: 1rem; }
  .mb-4 { margin-bottom: 1.5rem; }
  .mb-5 { margin-bottom: 3rem; }

  .mt-0 { margin-top: 0; }
  .mt-1 { margin-top: 0.25rem; }
  .mt-2 { margin-top: 0.5rem; }
  .mt-3 { margin-top: 1rem; }
  .mt-4 { margin-top: 1.5rem; }
  .mt-5 { margin-top: 3rem; }

  .p-0 { padding: 0; }
  .p-1 { padding: 0.25rem; }
  .p-2 { padding: 0.5rem; }
  .p-3 { padding: 1rem; }
  .p-4 { padding: 1.5rem; }
  .p-5 { padding: 3rem; }
`;

export default GlobalStyles;
